import * as React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Clock, Share2, ThumbsUp } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  TabsContent,
  TabsTrigger,
  Tabs,
  TabsList,
} from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import event3 from "../assets/event-3.jpg";
import BackButton from "../components/BackButton";
import { EventService, DynamicModuleService } from "../services/APIs";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import { Event } from "../utils/types/event";
import CommonModal from "../components/common/CommonModal";
import { Form, Input, InputNumber } from "antd";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

/// <reference types="google.maps" />

// Google Maps libraries
const libraries: ("places" | "geometry")[] = ["places", "geometry"];

// Helper function to format date range
const formatDateRange = (startDate: string, endDate: string): string => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    // Add ordinal suffix to day
    const getOrdinalSuffix = (n: number): string => {
      if (n > 3 && n < 21) {
        return "th";
      }
      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
  };

  const formattedStart = formatDate(startDate);
  const formattedEnd = formatDate(endDate);

  if (startDate === endDate) {
    return formattedStart;
  }

  return `${formattedStart} - ${formattedEnd}`;
};

// Helper function to format API date to display format
const formatApiDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();

  // Add ordinal suffix to day
  const getOrdinalSuffix = (n: number): string => {
    if (n > 3 && n < 21) {
      return "th";
    }
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};

// Helper function to format time from HH:MM:SS to HH:MM AM/PM
const formatTime = (timeString: string): string => {
  if (!timeString) {
    return "";
  }
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper function to format currency
const formatCurrency = (amount: string | number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return "$0";
  }
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) {
    return "$0";
  }
  return `$${numAmount.toLocaleString("en-US")}`;
};

// Show numeric amounts as currency; otherwise show API text as-is (e.g. "TBD")
const displaySponsorshipAmount = (
  amount: string | number | null | undefined
): string => {
  if (amount === null || amount === undefined) {
    return "—";
  }
  const s = String(amount).trim();
  if (!s) {
    return "—";
  }
  const normalized = s.replace(/,/g, "");
  const n = parseFloat(normalized);
  if (!isNaN(n) && isFinite(n) && /^[\d.]+$/.test(normalized)) {
    return formatCurrency(n);
  }
  return s;
};

// Helper function to calculate progress percentage
const calculateProgress = (
  raised: string | number | null | undefined,
  needed: string | number | null | undefined
): number => {
  if (
    raised === null ||
    raised === undefined ||
    needed === null ||
    needed === undefined
  ) {
    return 0;
  }
  const raisedNum = typeof raised === "string" ? parseFloat(raised) : raised;
  const neededNum = typeof needed === "string" ? parseFloat(needed) : needed;
  if (!neededNum || isNaN(raisedNum) || isNaN(neededNum)) {
    return 0;
  }
  return Math.round((raisedNum / neededNum) * 100);
};

// Helper function to calculate days left
const calculateDaysLeft = (
  _startDate: string | null | undefined,
  endDate: string | null | undefined
): number => {
  if (!endDate) {
    return 0;
  }
  try {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  } catch (error) {
    console.error("Error calculating days left:", error);
    return 0;
  }
};

// Helper function to format interest count
const formatInterestCount = (count: number): string => {
  if (count >= 1000000) {
    const millions = (count / 1000000).toFixed(1);
    return `${millions}M+`;
  } else if (count >= 1000) {
    const thousands = (count / 1000).toFixed(1);
    return `${thousands}K+`;
  }
  return `${count}+`;
};

const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eventId = id;

  const [event, setEvent] = React.useState<Event | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [agendaSessions, setAgendaSessions] = React.useState<any[]>([]);
  const [donations, setDonations] = React.useState<any[]>([]);
  const [volunteers, setVolunteers] = React.useState<any[]>([]);
  const [sponsorships, setSponsorships] = React.useState<any[]>([]);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [modalTitle, setModalTitle] = React.useState<string>("");
  const [form] = Form.useForm();
  const [interestLoading, setInterestLoading] = React.useState<boolean>(false);
  const [modalWidth, setModalWidth] = React.useState<number | string>(500);
  const [interestCount, setInterestCount] = React.useState<number>(0);
  const [isInterested, setIsInterested] = React.useState<boolean>(false);
  const [selectedDonationId, setSelectedDonationId] = React.useState<
    string | null
  >(null);
  const [selectedSponsorId, setSelectedSponsorId] = React.useState<
    string | null
  >(null);
  const [mapPosition, setMapPosition] = React.useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapRef, setMapRef] = React.useState<google.maps.Map | null>(null);
  const [participatedActivities, setParticipatedActivities] = React.useState<
    string[]
  >([]);
  const [volunteerLoading, setVolunteerLoading] = React.useState<string | null>(
    null
  );

  // Load Google Maps API
  const googleMapsApiKey =
    import.meta.env.VITE_GOOGLEPLACE_API_KEY ??
    "AIzaSyBBfoUsiF8ZX5iopPppB9x5XRriRaALhiY";
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleMapsApiKey,
    libraries,
  });

  // Fetch event details
  React.useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        console.log("EventDetail: No eventId provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log("EventDetail: Fetching event details for ID:", eventId);
        // First try to get event details from details endpoint
        const eventDetails = await EventService.getEventDetails(eventId, false);
        console.log("EventDetail: Event details response:", eventDetails);

        if (eventDetails) {
          setEvent(eventDetails);
          // Set interest status from API response - check both is_interested and interested fields
          const interestedStatus =
            (eventDetails as any).interested ??
            eventDetails.is_interested ??
            false;
          setIsInterested(interestedStatus);
          // Set interest count from API response
          const count = eventDetails.interested_count ?? 0;
          setInterestCount(count);
        } else {
          // Fallback: try to get from all events
          console.log("EventDetail: Trying fallback - getAllEvents");
          const allEvents = await EventService.getAllEvents(false);
          const foundEvent = allEvents.find(e => e.id === eventId);

          if (foundEvent) {
            setEvent(foundEvent);
            // Set interest status from API response - check both is_interested and interested fields
            const interestedStatus =
              (foundEvent as any).interested ??
              foundEvent.is_interested ??
              false;
            setIsInterested(interestedStatus);
            // Set interest count from API response
            const count = foundEvent.interested_count ?? 0;
            setInterestCount(count);
          } else {
            // Final fallback: try to get by ID if API supports it
            console.log("EventDetail: Trying final fallback - getEventById");
            const eventData = await EventService.getEventById(eventId, false);
            if (eventData) {
              setEvent(eventData);
              // Set interest status from API response - check both is_interested and interested fields
              const interestedStatus =
                (eventData as any).interested ??
                eventData.is_interested ??
                false;
              setIsInterested(interestedStatus);
              // Set interest count from API response
              const count = eventData.interested_count ?? 0;
              setInterestCount(count);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Fetch agenda sessions
  React.useEffect(() => {
    const fetchAgendaSessions = async () => {
      if (!eventId) {
        setAgendaSessions([]);
        return;
      }

      try {
        console.log(
          "EventDetail: Fetching agenda sessions for eventId:",
          eventId
        );
        const response = await DynamicModuleService.getGridCoByConfig(
          "MM_Agenda_Sessions",
          1,
          1000,
          [
            "id",
            "activityname",
            "starttime",
            "endtime",
            "date",
            "speakers",
            "venue",
            "description",
          ],
          {
            jdid: eventId,
          },
          false
        );
        console.log("EventDetail: Agenda sessions response:", response);

        if (response?.results) {
          setAgendaSessions(response.results);
        } else if (Array.isArray(response)) {
          setAgendaSessions(response);
        } else {
          setAgendaSessions([]);
        }
      } catch (error) {
        console.error("Error fetching agenda sessions:", error);
        setAgendaSessions([]);
      }
    };

    fetchAgendaSessions();
  }, [eventId]);

  // Fetch donations function - extracted to be reusable
  const fetchDonations = React.useCallback(async () => {
    if (!eventId) {
      setDonations([]);
      return;
    }

    try {
      console.log("EventDetail: Fetching donations for eventId:", eventId);
      const response = await DynamicModuleService.getGridCoByConfig(
        "MM_Donation",
        1,
        1000,
        [
          "id",
          "donationtitle",
          "category",
          "donationamountneeded",
          "donationamountraised",
          "descripction",
          "fromdate",
          "todate",
          "fromtime",
          "totime",
        ],
        {
          jdid: eventId,
        },
        false
      );
      console.log("EventDetail: Donations response:", response);

      if (response?.results) {
        setDonations(response.results);
      } else if (Array.isArray(response)) {
        setDonations(response);
      } else {
        setDonations([]);
      }
    } catch (error) {
      console.error("Error fetching donations:", error);
      setDonations([]);
    }
  }, [eventId]);

  // Fetch donations on mount and when eventId changes
  React.useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  // Fetch volunteers
  React.useEffect(() => {
    const fetchVolunteers = async () => {
      if (!eventId) {
        setVolunteers([]);
        return;
      }

      try {
        console.log("EventDetail: Fetching volunteers for eventId:", eventId);
        const response = await DynamicModuleService.getGridCoByConfig(
          "MM_Volunteers",
          1,
          1000,
          [
            "id",
            "activitytitle",
            "description",
            "fromtime",
            "totime",
            "fromdate",
            "todate",
            "noofvolunteers",
            "applyvolunteers",
          ],
          {
            jdid: eventId,
          },
          false
        );
        console.log("EventDetail: Volunteers response:", response);

        if (response?.results) {
          setVolunteers(response.results);
        } else if (Array.isArray(response)) {
          setVolunteers(response);
        } else {
          setVolunteers([]);
        }
      } catch (error) {
        console.error("Error fetching volunteers:", error);
        setVolunteers([]);
      }
    };

    fetchVolunteers();
  }, [eventId]);

  // Fetch volunteer signups for the current user to persist status on refresh
  React.useEffect(() => {
    const fetchUserSignups = async () => {
      try {
        const response = await EventService.getVolunteerSignups(false);
        if (response?.success && Array.isArray(response.data)) {
          const signupIds = response.data.map(
            (item: any) => item.volunteer_activity_id
          );
          setParticipatedActivities(signupIds);
        }
      } catch (error) {
        console.error("Error fetching user volunteer signups:", error);
      }
    };

    fetchUserSignups();
  }, []);

  // Fetch sponsorship packages (MM_Sponsorship) for this event
  React.useEffect(() => {
    const fetchSponsorships = async () => {
      if (!eventId) {
        setSponsorships([]);
        return;
      }

      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          "MM_Sponsorship",
          1,
          100,
          [
            "id",
            "sponsororganization",
            "packagee",
            "amount",
            "benefits",
            "is_active",
          ],
          {
            jdid: eventId,
          },
          false
        );

        const raw = response?.results
          ? response.results
          : Array.isArray(response)
            ? response
            : [];
        const list = Array.isArray(raw)
          ? raw.filter((row: any) => row?.is_active !== false)
          : [];
        setSponsorships(list);
      } catch (error) {
        console.error("Error fetching sponsorships:", error);
        setSponsorships([]);
      }
    };

    fetchSponsorships();
  }, [eventId]);

  // Geocode address when event and map are loaded
  React.useEffect(() => {
    const geocodeAddress = async () => {
      if (!event?.address || !isMapLoaded || !window.google?.maps) {
        return;
      }

      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: event.address }, (results, status) => {
          if (status === "OK" && results?.[0]) {
            const location = results[0].geometry.location;
            const latLng = {
              lat: location.lat(),
              lng: location.lng(),
            };
            setMapPosition(latLng);
          } else {
            console.error("Geocoding failed:", status);
            // Default to a center location if geocoding fails
            setMapPosition({ lat: 0, lng: 0 });
          }
        });
      } catch (error) {
        console.error("Error geocoding address:", error);
      }
    };

    geocodeAddress();
  }, [event?.address, isMapLoaded]);

  // Handle map load
  const onMapLoad = React.useCallback((map: google.maps.Map) => {
    setMapRef(map);
  }, []);

  // Center map when position is available
  React.useEffect(() => {
    if (mapRef && mapPosition) {
      const latLng = new window.google.maps.LatLng(
        mapPosition.lat,
        mapPosition.lng
      );
      mapRef.setCenter(latLng);
      mapRef.setZoom(15);
    }
  }, [mapRef, mapPosition]);

  // Handle modal actions
  const handleModalOpen = (
    title: string,
    donationId?: string | null,
    sponsorId?: string | null
  ) => {
    setModalTitle(title);
    setModalOpen(true);
    if (title === "Donate Now" && donationId) {
      setSelectedDonationId(donationId);
    } else {
      setSelectedDonationId(null);
    }
    if (title === "Sponsor" && sponsorId) {
      setSelectedSponsorId(sponsorId);
    } else {
      setSelectedSponsorId(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    form.resetFields();
    setSelectedDonationId(null);
    setSelectedSponsorId(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      console.log("Form values:", values);

      // Handle donation submission
      if (modalTitle === "Donate Now" && eventId) {
        // Format donation amount to string with 2 decimal places
        const donationAmount = values.amount
          ? parseFloat(String(values.amount)).toFixed(2)
          : "";

        const donationPayload: any = {
          event_id: eventId,
          name: values.name,
          email: values.email,
          phone: values.phone,
          donation_amount: donationAmount,
          message: values.message ?? undefined,
        };

        // Include donation_id if a specific donation card was selected
        if (selectedDonationId) {
          donationPayload.donation_id = selectedDonationId;
        }

        await EventService.submitDonation(donationPayload, true);

        // Refetch donations to update the card values immediately
        await fetchDonations();

        handleModalClose();
        return;
      }

      if (modalTitle === "Sponsor" && eventId) {
        if (!selectedSponsorId) {
          console.error("Sponsor package id is missing");
          return;
        }
        const rawAmount = values.sponsor_amount;
        const sponsorAmountNum =
          typeof rawAmount === "number"
            ? rawAmount
            : parseFloat(String(rawAmount ?? "").replace(/,/g, ""));
        if (Number.isNaN(sponsorAmountNum)) {
          throw new Error("Invalid sponsor amount");
        }

        await EventService.submitSponsor(
          {
            event_id: eventId,
            sponsor_id: selectedSponsorId,
            name: values.name,
            email: values.email,
            phone: String(values.phone ?? "").trim(),
            sponsor_amount: sponsorAmountNum,
            message:
              values.message !== undefined && values.message !== ""
                ? String(values.message)
                : undefined,
          },
          true
        );

        handleModalClose();
        return;
      }

      // Handle other modal submissions (Participate) here if needed
      handleModalClose();
    } catch (error) {
      console.error("Validation or submission failed:", error);
    }
  };

  // Handle interested button click
  const handleInterested = async () => {
    if (!eventId) {
      console.error("Event ID is missing");
      return;
    }

    try {
      setInterestLoading(true);
      const response = await EventService.expressEventInterest(
        eventId,
        true,
        false
      );

      // Check API response structure
      const interestedStatus =
        response?.data?.interested ??
        response?.interested ??
        response?.data?.data?.interested ??
        response?.data?.is_interested;
      const interestedCount =
        response?.data?.interested_count ??
        response?.interested_count ??
        response?.data?.data?.interested_count;

      // If API returns interested === true, update state accordingly
      if (interestedStatus === true) {
        setIsInterested(true);
        if (interestedCount !== undefined && interestedCount !== null) {
          setInterestCount(interestedCount);
        } else {
          setInterestCount(prev => prev + 1);
        }
      } else {
        setIsInterested(true);
        setInterestCount(prev => prev + 1);
      }

      // Refresh event details to get updated count from API
      const updatedEvent = await EventService.getEventDetails(eventId, false);
      if (updatedEvent) {
        const count = updatedEvent.interested_count ?? 0;
        setInterestCount(count);
        const status =
          (updatedEvent as any).interested ??
          updatedEvent.is_interested ??
          true;
        setIsInterested(status);
      }
    } catch (error) {
      console.error("Error expressing interest:", error);
    } finally {
      setInterestLoading(false);
    }
  };

  // Handle undo button click
  const handleUndo = async () => {
    if (!eventId) {
      console.error("Event ID is missing");
      return;
    }

    try {
      setInterestLoading(true);
      const response = await EventService.expressEventInterest(
        eventId,
        false,
        false
      );

      // Check API response structure
      const interestedStatus =
        response?.data?.interested ??
        response?.interested ??
        response?.data?.data?.interested ??
        response?.data?.is_interested;
      const interestedCount =
        response?.data?.interested_count ??
        response?.interested_count ??
        response?.data?.data?.interested_count;

      // If API returns interested === false, update state accordingly
      if (
        interestedStatus === false ||
        interestedStatus === undefined ||
        interestedStatus === null
      ) {
        setIsInterested(false);
        if (interestedCount !== undefined && interestedCount !== null) {
          setInterestCount(interestedCount);
        } else {
          setInterestCount(prev => Math.max(0, prev - 1));
        }
      } else {
        setIsInterested(false);
        setInterestCount(prev => Math.max(0, prev - 1));
      }

      // Refresh event details to get updated count from API
      const updatedEvent = await EventService.getEventDetails(eventId, false);
      if (updatedEvent) {
        const count = updatedEvent.interested_count ?? 0;
        setInterestCount(count);
        const status =
          (updatedEvent as any).interested ??
          updatedEvent.is_interested ??
          false;
        setIsInterested(status);
      }
    } catch (error) {
      console.error("Error undoing interest:", error);
    } finally {
      setInterestLoading(false);
    }
  };

  // Handle volunteer signup
  const handleVolunteerSignup = async (activityId: string) => {
    if (!activityId) return;
    try {
      setVolunteerLoading(activityId);
      const response = await EventService.volunteerSignup(activityId, true);
      console.log("Volunteer signup response:", response);
      if (response?.success) {
        setParticipatedActivities(prev => [...prev, activityId]);
        // Update local volunteer count for immediate UI feedback
        setVolunteers(prev =>
          prev.map(v =>
            v.id === activityId
              ? { ...v, applyvolunteers: (v.applyvolunteers ?? 0) + 1 }
              : v
          )
        );
      }
    } catch (error) {
      console.error("Error signing up for volunteer activity:", error);
    } finally {
      setVolunteerLoading(null);
    }
  };

  // Handle modal width for mobile responsiveness
  React.useEffect(() => {
    const updateModalWidth = () => {
      setModalWidth(window.innerWidth < 640 ? "90%" : 500);
    };
    updateModalWidth();
    window.addEventListener("resize", updateModalWidth);
    return () => window.removeEventListener("resize", updateModalWidth);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background p-4">
        <div className="text-muted-foreground">Loading event details...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-background p-4">
        <div className="mb-4 text-muted-foreground">Event not found</div>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const imageUrl =
    event.event_banner && event.event_banner.length > 0
      ? event.event_banner[0]
      : event3;

  const location = event.address ?? "Location not specified";
  const dateRange = formatDateRange(event.start_date, event.end_date);
  const availableSeats = event.overall_capacity ?? 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div>
              <h1 className="text-lg font-bold text-primary">
                {event.event_name}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-64 overflow-hidden md:h-80">
        <img
          src={imageUrl}
          alt={event.event_name}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50" />
      </div>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="space-y-6 p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {location}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="mt-0.5 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {dateRange}
                      </p>
                    </div>
                  </div>

                  <div className="border-b border-t py-4 text-center">
                    <p className="text-4xl font-bold text-primary">
                      {availableSeats}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Seats Available
                    </p>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <ThumbsUp className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-semibold">
                        {formatInterestCount(interestCount)} are interested
                      </p>
                    </div>
                  </div>
                  <h3 className="mb-2 font-bold text-foreground">ABOUT US</h3>
                  <p className="mb-3 text-sm text-muted-foreground">
                    {event.description ?? "No description available"}
                  </p>
                  {!isInterested ? (
                    <Button
                      className="w-full"
                      variant="secondary"
                      onClick={handleInterested}
                      disabled={interestLoading || !eventId}
                    >
                      {interestLoading ? "Loading..." : "Interested"}
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={handleUndo}
                      disabled={interestLoading || !eventId}
                    >
                      {interestLoading ? "Loading..." : "Undo"}
                    </Button>
                  )}
                </div>

                <div>
                  <h3 className="mb-3 font-bold text-foreground">Location</h3>
                  <div className="h-40 w-full overflow-hidden rounded-lg bg-muted">
                    {mapLoadError ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="text-sm text-muted-foreground">
                          Error loading map
                        </div>
                      </div>
                    ) : !isMapLoaded || !mapPosition ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="text-sm text-muted-foreground">
                          Loading map...
                        </div>
                      </div>
                    ) : (
                      <GoogleMap
                        mapContainerStyle={{
                          width: "100%",
                          height: "100%",
                        }}
                        center={mapPosition}
                        zoom={15}
                        onLoad={onMapLoad}
                        options={{
                          disableDefaultUI: true,
                          zoomControl: true,
                          streetViewControl: false,
                          mapTypeControl: false,
                          fullscreenControl: false,
                        }}
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            <div>
              <p className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                EVENT INFORMATION
              </p>

              <Tabs defaultValue="agenda" className="w-full">
                <TabsList className="mb-6 w-full justify-start overflow-x-auto overflow-y-hidden h-auto p-1 bg-muted/50 scrollbar-hide">
                  <TabsTrigger value="agenda" className="px-4 py-2">Agenda</TabsTrigger>
                  <TabsTrigger value="donation" className="px-4 py-2">Donation</TabsTrigger>
                  <TabsTrigger value="sponsorship" className="px-4 py-2">Sponsorship</TabsTrigger>
                  <TabsTrigger value="volunteering" className="px-4 py-2">Volunteering</TabsTrigger>
                </TabsList>

                <TabsContent value="agenda" className="space-y-4">
                  {/* Agenda Items */}
                  {agendaSessions.length > 0 ? (
                    agendaSessions.map(session => (
                      <Card key={session.id}>
                        <CardContent className="p-6">
                          <h3 className="mb-3 text-lg font-bold text-foreground">
                            {session.activityname ?? "Untitled Session"}
                          </h3>
                          <div className="mb-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                            {session.date && (
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>
                                  {formatApiDate(session.date) || "Date TBD"}
                                </span>
                              </div>
                            )}
                            {session.starttime && session.endtime && (
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>
                                  {formatTime(session.starttime)} -{" "}
                                  {formatTime(session.endtime)}
                                </span>
                              </div>
                            )}
                          </div>
                          {(session.venue ?? session.speakers) && (
                            <p className="mb-3 text-sm text-muted-foreground">
                              {session.venue && `• ${session.venue}`}
                              {session.speakers && ` • ${session.speakers}`}
                            </p>
                          )}
                          {session.description && (
                            <p className="text-muted-foreground">
                              {session.description}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-center text-muted-foreground">
                          No agenda sessions available
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      className="flex-1"
                      size="lg"
                      onClick={() => {
                        if (event.is_registration_open) {
                          navigate(`${CitycollabNavigationRoutes.EVENT_TICKETS}?id=${eventId}`);
                        }
                      }}
                      disabled={!event.is_registration_open}
                    >
                      {event.is_registration_open
                        ? "Buy Ticket"
                        : (event.registration_status ?? "Registration Closed")}
                    </Button>
                    <Button size="icon" variant="outline">
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="donation" className="space-y-4">
                  {donations.length > 0 ? (
                    donations.map(donation => {
                      const progress = calculateProgress(
                        donation.donationamountraised,
                        donation.donationamountneeded
                      );
                      const raisedFormatted = formatCurrency(
                        donation.donationamountraised
                      );
                      const neededFormatted = formatCurrency(
                        donation.donationamountneeded
                      );

                      // Calculate days left
                      const daysLeft = calculateDaysLeft(
                        donation.fromdate,
                        donation.todate
                      );

                      // Format date range
                      const dateRange =
                        donation.fromdate && donation.todate
                          ? formatDateRange(donation.fromdate, donation.todate)
                          : donation.fromdate
                            ? formatApiDate(donation.fromdate)
                            : null;

                      // Format time range
                      const timeRange =
                        donation.fromtime && donation.totime
                          ? `${formatTime(donation.fromtime)} - ${formatTime(donation.totime)}`
                          : donation.fromtime
                            ? formatTime(donation.fromtime)
                            : null;

                      return (
                        <Card key={donation.id}>
                          <CardContent className="p-6">
                            <div className="mb-3 flex items-start justify-between">
                              <div>
                                <h3 className="text-lg font-bold text-foreground">
                                  {donation.donationtitle ??
                                    "Untitled Donation"}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {donation.category ?? "General"}
                                </p>
                              </div>
                              <Button
                                onClick={() =>
                                  handleModalOpen("Donate Now", donation.id)
                                }
                              >
                                Donate Now
                              </Button>
                            </div>
                            {(dateRange ?? timeRange) && (
                              <div className="mb-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                                {dateRange && (
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    <span>{dateRange}</span>
                                  </div>
                                )}
                                {timeRange && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    <span>{timeRange}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <p className="mb-2 text-lg font-bold text-primary">
                              Donation needed {neededFormatted}
                            </p>
                            <p
                              className="mb-2 text-sm font-semibold"
                              style={{ color: "hsl(var(--primary))" }}
                            >
                              Donated: {raisedFormatted} of {neededFormatted}
                            </p>
                            <Progress value={progress} className="mb-3" />
                            {donation.descripction && (
                              <p className="text-muted-foreground">
                                {donation.descripction}
                              </p>
                            )}
                            <div className="mt-3 flex gap-4 text-sm">
                              <div>
                                <span className="font-semibold">
                                  {progress}%
                                </span>{" "}
                                Funded
                              </div>
                              <div>
                                <span className="font-semibold">
                                  {daysLeft}
                                </span>{" "}
                                Days Left
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-center text-muted-foreground">
                          No donations available
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="sponsorship" className="space-y-4">
                  {sponsorships.length > 0 ? (
                    sponsorships.map((sponsor: any) => (
                      <Card key={sponsor.id}>
                        <CardContent className="p-6">
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-lg font-bold text-foreground">
                                {sponsor.packagee ?? "Sponsorship package"}
                              </h3>
                              {sponsor.sponsororganization && (
                                <p className="text-sm text-muted-foreground">
                                  {sponsor.sponsororganization}
                                </p>
                              )}
                              <p className="text-lg font-bold text-primary">
                                {displaySponsorshipAmount(sponsor.amount)}
                              </p>
                            </div>
                            <Button
                              onClick={() =>
                                handleModalOpen("Sponsor", null, sponsor.id)
                              }
                            >
                              Sponsor
                            </Button>
                          </div>
                          <div className="mb-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 shrink-0" />
                              <span>{dateRange}</span>
                            </div>
                          </div>
                          {sponsor.benefits && (
                            <p className="text-muted-foreground">
                              {sponsor.benefits}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-center text-muted-foreground">
                          No sponsorship packages available
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="volunteering" className="space-y-4">
                  {volunteers.length > 0 ? (
                    volunteers.map(volunteer => {
                      const dateRange =
                        volunteer.fromdate && volunteer.todate
                          ? formatDateRange(
                            volunteer.fromdate,
                            volunteer.todate
                          )
                          : volunteer.fromdate
                            ? formatApiDate(volunteer.fromdate)
                            : "Date TBD";
                      const timeRange =
                        volunteer.fromtime && volunteer.totime
                          ? `${formatTime(volunteer.fromtime)} - ${formatTime(volunteer.totime)}`
                          : volunteer.fromtime
                            ? formatTime(volunteer.fromtime)
                            : "Time TBD";

                      // Calculate volunteer progress
                      const progress = calculateProgress(
                        volunteer.applyvolunteers,
                        volunteer.noofvolunteers
                      );
                      const appliedVolunteers = volunteer.applyvolunteers ?? 0;
                      const totalVolunteers = volunteer.noofvolunteers ?? 0;

                      return (
                        <Card key={volunteer.id}>
                          <CardContent className="p-6">
                            <div className="mb-3 flex items-start justify-between">
                              <h3 className="text-lg font-bold text-foreground">
                                {volunteer.activitytitle ?? "Untitled Activity"}
                              </h3>
                              {participatedActivities.includes(volunteer.id) ? (
                                <span className="text-sm font-semibold text-green-600">
                                  Participate Successfully
                                </span>
                              ) : (
                                <Button
                                  onClick={() =>
                                    handleVolunteerSignup(volunteer.id)
                                  }
                                  disabled={volunteerLoading === volunteer.id}
                                >
                                  {volunteerLoading === volunteer.id
                                    ? "Signing up..."
                                    : "Participate"}
                                </Button>
                              )}
                            </div>
                            <div className="mb-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{dateRange}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>{timeRange}</span>
                              </div>
                            </div>
                            {totalVolunteers > 0 && (
                              <div className="mb-3">
                                <p className="mb-2 text-sm font-semibold">
                                  Volunteers: {appliedVolunteers}/
                                  {totalVolunteers}
                                </p>
                                <Progress value={progress} />
                              </div>
                            )}
                            {volunteer.description && (
                              <p className="text-muted-foreground">
                                {volunteer.description}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-center text-muted-foreground">
                          No volunteer opportunities available
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      <CommonModal
        open={modalOpen}
        title={modalTitle}
        onOk={handleModalOk}
        onCancel={handleModalClose}
        okText="Submit"
        cancelText="Cancel"
        width={modalWidth}
        maskClosable={false}
        keyboard={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {/* Common Fields */}
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>

          {/* Donation Specific Fields */}
          {modalTitle === "Donate Now" && (
            <>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  {
                    len: 10,
                    message: "Phone number must be exactly 10 digits",
                  },
                ]}
              >
                <Input
                  placeholder="Enter your phone number"
                  maxLength={10}
                  onKeyPress={e => {
                    // Only allow numbers
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="amount"
                label="Donation Amount"
                rules={[
                  { required: true, message: "Please enter donation amount" },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  min={1}
                  formatter={value =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={value => value?.replace(/\$\s?|(,*)/g, "") as any}
                />
              </Form.Item>
              <Form.Item name="message" label="Message (Optional)">
                <Input.TextArea
                  placeholder="Add a message with your donation"
                  rows={4}
                />
              </Form.Item>
            </>
          )}

          {/* Sponsorship Specific Fields */}
          {modalTitle === "Sponsor" && (
            <>
              <Form.Item
                name="sponsor_amount"
                label="sponsor_amount"
                rules={[
                  { required: true, message: "Please enter sponsor amount" },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  formatter={value =>
                    value !== undefined && value !== null
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                  parser={value => {
                    const n = value?.replace(/,/g, "");
                    return (n === "" || n === undefined
                      ? undefined
                      : (Number(n) as any)) as any;
                  }}
                />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Contact Number"
                rules={[
                  { required: true, message: "Please enter contact number" },
                ]}
              >
                <Input placeholder="Enter contact number" />
              </Form.Item>
              <Form.Item name="message" label="Additional Information">
                <Input.TextArea
                  placeholder="Tell us about your organization"
                  rows={4}
                />
              </Form.Item>
            </>
          )}

          {/* Volunteering Specific Fields */}
          {modalTitle === "Participate" && (
            <>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                ]}
              >
                <Input placeholder="Enter your phone number" />
              </Form.Item>
              <Form.Item
                name="availability"
                label="Availability"
                rules={[
                  { required: true, message: "Please enter your availability" },
                ]}
              >
                <Input.TextArea
                  placeholder="When are you available to volunteer?"
                  rows={3}
                />
              </Form.Item>
              <Form.Item
                name="previous_experience"
                label="Previous Experience (Optional)"
              >
                <Input.TextArea
                  placeholder="Share any relevant volunteer experience"
                  rows={3}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </CommonModal>
    </div>
  );
};

export default EventDetail;
